import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { KTSVG } from '../../../../../../_metronic/helpers'
import { MyPolicy } from '../../../../auth/core/_models'
import {
  EnrollmentApiResponse,
  UnifiedApiRawItem,
  PolicyRulesJson,
  EmployeeData,
  ExistingMember,
  UploadDataRecord,
  RelationType,
} from '../types'
import UnifiedEnrollmentForm from './UnifiedEnrollmentForm'
import ReadOnlyView from './ReadOnlyView'
import UploadDataFallbackView from './UploadDataFallbackView'

const UNIFIED_ENDPOINT = 'https://etl.futurisk.in/webhook/9979887a-6b90-44c2-b145-1e3cac5c1e02'

/**
 * Map raw relation strings from upload/enrolled data into the canonical
 * RelationType values used by the policy rules engine.
 * Mirrors the normalisation logic in ACS / Williamslea / Taskus forms.
 */
function normalizeRelation(raw: string): RelationType {
  const r = (raw || '').trim()
  const rLower = r.toLowerCase()

  if (rLower === 'self') return 'Self'
  if (['husband', 'wife', 'spouse'].includes(rLower)) return 'Spouse'
  if (['son', 'daughter', 'child'].includes(rLower)) return 'Child'
  // "law" catch-all must come before plain parent check (matches father-in-law, motherinlaw, etc.)
  if (rLower.includes('law')) return 'ParentInLaw'
  if (['father', 'mother', 'parent'].includes(rLower)) return 'Parent'
  if (['sibling'].includes(rLower)) return 'Sibling'
  if (['brother'].includes(rLower)) return 'Brother'
  if (['sister'].includes(rLower)) return 'Sister'

  // Fallback: return as-is and let the form handle it
  return r as RelationType
}

/**
 * Ensure UploadData / EnrolledData is always an array.
 * Handles: JSON array, single object, "{}", empty string.
 */
function toRecordArray(raw: string | null | undefined): UploadDataRecord[] {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) {
      // Filter out empty objects like {} that carry no meaningful data
      return (parsed as UploadDataRecord[]).filter(
        (r) => r && typeof r === 'object' && Object.keys(r).length > 0
      )
    }
    if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
      return [parsed as UploadDataRecord]
    }
    return []
  } catch {
    return []
  }
}

/**
 * Map an UploadDataRecord (or EnrolledData with same shape) into EmployeeData
 * so the form can consume it unchanged.
 */
function toEmployeeData(rec: UploadDataRecord, freeze: boolean): EmployeeData {
  return {
    EmployeeNumber: rec.EmployeeID,
    EmployeeName: rec.Name,
    Email: rec.EmployeeID,
    DOB: rec.DOB,
    Gender: rec.Gender,
    Grade: rec.Grade,
    CorporateCode: rec.CorporateCode,
    DOJ: rec.JoiningDate,
    CTC: null,
    freezeEnrolment: freeze,
  }
}


/**
 * Map an UploadDataRecord (or EnrolledData) into an ExistingMember stub
 * so the form can prefill family rows during re-edit / reopened cases.
 * Normalises the Relation field to canonical RelationType.
 */
function toExistingMember(rec: UploadDataRecord): ExistingMember {
  return {
    Id: String(rec.sid),
    EmployeeNumber: rec.EmployeeID,
    Name: rec.Name,
    Dob: rec.DOB,
    Relation: normalizeRelation(rec.Relation),
    Gender: rec.Gender,
    Premium: 0,
    Grade: rec.Grade,
    CrmClientId: rec.CorpCode,
    PremiumOpted: null,
    AdditionalPremium: null,
    LivesCharged: null,
    GradeLower: null,
    TopupName: null,
    TopupSumInsured: null,
    TopupBasePremium: null,
    TopupGst: null,
    TopupTotalPremium: null,
    FlexiName: null,
    FlexiBasePremium: null,
    FlexiGst: null,
    FlexiTotalPremium: null,
    BasePremium: null,
    BaseGst: null,
    BaseTotalPremium: null,
    TotalPremiumPayable: null,
    TotalGst: null,
    BaseSumInsured: rec.SumInsured ? Number(rec.SumInsured) : null,
    CreatedAt: rec.UpdateDate,
  }
}

/**
 * Map enrolled data (which has premium/flexi/topup fields) into ExistingMember.
 * EnrolledData records have a different shape from UploadDataRecord, so we
 * read the premium fields directly from the raw object.
 */
function toExistingMemberFromEnrolled(rec: any): ExistingMember {
  return {
    Id: String(rec.Id ?? rec.sid ?? ''),
    EmployeeNumber: rec.EmployeeNumber ?? rec.EmployeeID ?? '',
    Name: rec.Name ?? '',
    Dob: rec.Dob ?? rec.DOB ?? '',
    Relation: normalizeRelation(rec.Relation ?? ''),
    Gender: rec.Gender ?? '',
    Premium: rec.Premium ?? 0,
    Grade: rec.Grade ?? '',
    CrmClientId: rec.CrmClientId ?? rec.CorpCode ?? '',
    PremiumOpted: rec.PremiumOpted ?? null,
    AdditionalPremium: rec.AdditionalPremium ?? null,
    LivesCharged: rec.LivesCharged ?? null,
    GradeLower: rec.GradeLower ?? null,
    TopupName: rec.TopupName ?? null,
    TopupSumInsured: rec.TopupSumInsured ?? null,
    TopupBasePremium: rec.TopupBasePremium ?? null,
    TopupGst: rec.TopupGst ?? null,
    TopupTotalPremium: rec.TopupTotalPremium ?? null,
    FlexiName: rec.FlexiName ?? null,
    FlexiBasePremium: rec.FlexiBasePremium ?? null,
    FlexiGst: rec.FlexiGst ?? null,
    FlexiTotalPremium: rec.FlexiTotalPremium ?? null,
    BasePremium: rec.BasePremium ?? null,
    BaseGst: rec.BaseGst ?? null,
    BaseTotalPremium: rec.BaseTotalPremium ?? null,
    TotalPremiumPayable: rec.TotalPremiumPayable ?? null,
    TotalGst: rec.TotalGst ?? null,
    BaseSumInsured: rec.BaseSumInsured ?? (rec.SumInsured ? Number(rec.SumInsured) : null),
    CreatedAt: rec.CreatedAt ?? rec.UpdateDate ?? '',
  }
}

/**
 * Parse the unified API response array into the shape the rest of the
 * enrollment components already expect (EnrollmentApiResponse).
 *
 * Rules:
 * - Freeze=true  → read-only display using EnrolledData
 * - Freeze=false & EnrolledData has data → reopened case, prefill with EnrolledData
 * - Freeze=false & EnrolledData empty   → fresh enrollment, prefill with UploadData
 *
 * Both UploadData and EnrolledData may be a single record OR an array of records
 * (Self + dependents). All dependents are mapped into existingMembers so the form
 * can pre-fill family rows.
 */
function parseUnifiedResponse(items: UnifiedApiRawItem[]): EnrollmentApiResponse {
  const first = items[0]
  const policyRulesJson: PolicyRulesJson = JSON.parse(first.PolicySetup)
  const freeze = first.Freeze

  const uploadRecords: UploadDataRecord[] = toRecordArray(first.UploadData)
  const enrolledRaw: any[] = (() => {
    if (!first.EnrolledData) return []
    try {
      const parsed = JSON.parse(first.EnrolledData)
      if (Array.isArray(parsed)) {
        return parsed.filter((r: any) => r && typeof r === 'object' && Object.keys(r).length > 0)
      }
      if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) return [parsed]
      return []
    } catch { return [] }
  })()
  const enrolledRecords: UploadDataRecord[] = toRecordArray(first.EnrolledData)

  // Determine which data set to use for prefilling
  const prefillRecords = enrolledRecords.length > 0 ? enrolledRecords : uploadRecords

  // Find the Self record for EmployeeData; fall back to first record
  const selfRecord =
    prefillRecords.find((r) => normalizeRelation(r.Relation) === 'Self') || prefillRecords[0]

  if (!selfRecord) {
    throw new Error('No employee (Self) record found in upload data')
  }

  const employeeData = toEmployeeData(selfRecord, freeze)

  // Build existingMembers from all records (Self + dependents)
  let existingMembers: ExistingMember[] | null = null

  if (freeze && enrolledRaw.length > 0) {
    // Frozen → display enrolled data as read-only (preserve premium fields)
    existingMembers = enrolledRaw.map(toExistingMemberFromEnrolled)
  } else if (!freeze && enrolledRecords.length > 0) {
    // Reopened → prefill form with enrolled data
    existingMembers = enrolledRecords.map(toExistingMember)
  } else if (!freeze && uploadRecords.length > 1) {
    // Fresh enrollment with dependents in UploadData → prefill all members
    existingMembers = uploadRecords.map(toExistingMember)
  }
  // else: fresh enrollment, single Self record → existingMembers stays null,
  //       form will init Self from employeeData

  return { policyRulesJson, employeeData, existingMembers, uploadRecords }
}

type Props = {
  show: boolean
  handleClose: () => void
  Title: string
  Email: string | undefined
  CrmUrl: string | undefined
  MyPolicy: MyPolicy | null
}

export const UnifiedEnrollmentListModel: React.FC<Props> = ({
  show,
  handleClose,
  Title,
  Email,
  CrmUrl,
  MyPolicy: myPolicy,
}) => {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [apiData, setApiData] = useState<EnrollmentApiResponse | null>(null)

  const crmDbName = myPolicy?.Policy.CrmDbName

  useEffect(() => {
    if (!show) {
      setApiData(null)
      setError(null)
      return
    }

    let cancelled = false
    const fetchData = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await fetch(UNIFIED_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            Email: Email,
            CorporateCode: crmDbName,
          }),
        })
        if (!response.ok) throw new Error('Failed to fetch enrollment data')
        const rawItems: UnifiedApiRawItem[] = await response.json()
        if (!rawItems || rawItems.length === 0) throw new Error('No enrollment records found')
        const data = parseUnifiedResponse(rawItems)
        if (!cancelled) setApiData(data)
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'An unexpected error occurred')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchData()
    return () => {
      cancelled = true
    }
  }, [show, Email, crmDbName, CrmUrl])

  const dismissModal = () => {
    handleClose()
  }

  const isFrozen = apiData?.employeeData?.freezeEnrolment === true

  return (
    <Modal
      className='modal fade'
      id='kt_modal_unified_enrollment'
      data-backdrop='static'
      tabIndex={-1}
      role='dialog'
      show={show}
      dialogClassName='modal-xl modal-dialog-scrollable'
      aria-hidden='true'
      onHide={dismissModal}
    >
      <div className='modal-content'>
        <div className='modal-header'>
          <h5 className='modal-title'>{isFrozen ? Title : 'Enroll Family'}</h5>
          <div
            className='btn btn-icon btn-sm btn-active-light-primary ms-2'
            onClick={dismissModal}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2x' />
          </div>
        </div>
        <div className='modal-body'>
          {loading ? (
            <div className='text-center py-10'>
              <div className='spinner-border text-primary' role='status'>
                <span className='sr-only'>Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className='text-danger text-center py-6'>{error}</div>
          ) : apiData ? (
            isFrozen ? (
              apiData.existingMembers && apiData.existingMembers.length > 0 ? (
                <ReadOnlyView members={apiData.existingMembers} policyRulesJson={apiData.policyRulesJson} />
              ) : (
                <UploadDataFallbackView uploadRecords={apiData.uploadRecords} />
              )
            ) : (
              <UnifiedEnrollmentForm
                policyRulesJson={apiData.policyRulesJson}
                employeeData={apiData.employeeData}
                existingMembers={apiData.existingMembers}
                policyCrmDbName={crmDbName}
                onSuccess={dismissModal}
              />
            )
          ) : (
            <div className='text-muted text-center py-6'>No data available</div>
          )}
        </div>
      </div>
    </Modal>
  )
}
